import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { apiAuth } from "@/lib/api";

const CONTACT_TYPE_LABEL = {
  founder: "Founder",
  investor: "Investor",
  partner: "Partner",
  journalist: "Journalist",
  other: "Sonstige",
};

const COLOR = {
  founder: "bg-sageMint",
  investor: "bg-skyMist",
  partner: "bg-butterCream",
  journalist: "bg-roseMilk",
  other: "bg-ivory",
};

export default function AdminContactsList() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const limit = Number(params.get("limit") || 25);
  const type = params.get("type") || "";
  const q = params.get("q") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftQ, setDraftQ] = useState(q);

  const query = useMemo(() => {
    const s = new URLSearchParams();
    s.set("page", String(page));
    s.set("limit", String(limit));
    if (type) s.set("type", type);
    if (q) s.set("q", q);
    return s.toString();
  }, [page, limit, type, q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiAuth.get(`/admin/contacts?${query}`).then(({ data }) => {
      if (!cancelled) { setData(data); setLoading(false); }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query]);

  const updateParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "" || v == null) next.delete(k); else next.set(k, String(v));
    next.set("page", "1");
    setParams(next);
  };

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="flex flex-col gap-5" data-testid="page-admin-contacts">
      <div className="bg-white border border-sandBeige rounded-3xl p-4 flex flex-wrap items-center gap-3">
        <form
          onSubmit={(e) => { e.preventDefault(); updateParam("q", draftQ.trim() || ""); }}
          className="flex items-center gap-2 flex-1 min-w-[200px]"
        >
          <div className="flex items-center gap-2 bg-porcelain rounded-2xl border border-sandBeige px-3 py-2 flex-1">
            <Search size={14} className="text-warmGrey" />
            <input
              className="bg-transparent outline-none text-sm flex-1"
              placeholder="Suche Name / E-Mail …"
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              data-testid="contacts-search"
            />
          </div>
        </form>
        <select
          className="wit-input max-w-xs"
          value={type}
          onChange={(e) => updateParam("type", e.target.value)}
          data-testid="filter-type"
        >
          <option value="">Alle Typen</option>
          {Object.entries(CONTACT_TYPE_LABEL).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-sandBeige rounded-3xl overflow-hidden">
        <table className="w-full hidden md:table">
          <thead className="bg-porcelain text-warmGrey">
            <tr>
              <th className="text-left text-xs font-medium px-5 py-3">Name</th>
              <th className="text-left text-xs font-medium px-4 py-3">E-Mail</th>
              <th className="text-left text-xs font-medium px-4 py-3">Typ</th>
              <th className="text-left text-xs font-medium px-4 py-3">Standort</th>
              <th className="text-left text-xs font-medium px-4 py-3">Quelle</th>
              <th className="text-left text-xs font-medium px-4 py-3">Erfasst</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sandBeige/60">
            {loading && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-warmGrey">Lade …</td></tr>
            )}
            {!loading && data?.items?.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-warmGrey">Keine Treffer.</td></tr>
            )}
            {data?.items?.map((c) => (
              <tr key={c.id} className="hover:bg-ivory/60 transition-colors">
                <td className="px-5 py-3.5 font-medium text-cocoa">{c.first_name} {c.last_name}</td>
                <td className="px-4 py-3.5 text-sm text-cocoa">{c.email}</td>
                <td className="px-4 py-3.5">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${COLOR[c.type] || "bg-ivory"}`}>
                    {CONTACT_TYPE_LABEL[c.type] || c.type}
                  </span>
                </td>
                <td className="px-4 py-3.5 text-xs text-cocoa">{c.city}, {c.country}</td>
                <td className="px-4 py-3.5 text-xs text-warmGrey">{c.source_form}</td>
                <td className="px-4 py-3.5 text-xs text-warmGrey">{c.created_at ? new Date(c.created_at).toLocaleDateString("de-DE") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="md:hidden divide-y divide-sandBeige/60">
          {!loading && data?.items?.length === 0 && (
            <p className="px-5 py-10 text-center text-warmGrey">Keine Treffer.</p>
          )}
          {data?.items?.map((c) => (
            <div key={c.id} className="px-5 py-4">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-cocoa truncate">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-warmGrey truncate">{c.email}</p>
                  <p className="text-xs text-warmGrey mt-1">{c.city}, {c.country}</p>
                </div>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] ${COLOR[c.type] || "bg-ivory"}`}>
                  {CONTACT_TYPE_LABEL[c.type] || c.type}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-warmGrey">
        <span>{total} Treffer</span>
        <div className="flex items-center gap-3">
          <button type="button" disabled={page <= 1} onClick={() => updateParam("page", page - 1)} className="wit-pill bg-white border-sandBeige text-cocoa disabled:opacity-40">‹ Prev</button>
          <span className="text-cocoa">{page} / {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => updateParam("page", page + 1)} className="wit-pill bg-white border-sandBeige text-cocoa disabled:opacity-40">Next ›</button>
        </div>
      </div>
    </div>
  );
}
